import {useState} from 'react';
import {Link as RouterLink, Redirect} from 'react-router-dom';

import {AiFeatureType} from 'nutshell-graphql-types';

import {AiFeatureGateModal} from '../../../../../../ui/ai-home/ai-feature-gate-modal/ai-feature-gate-modal';

import {colors} from '../../colors/colors';
import {Divider} from '../../layout/divider';
import {StackLegacy} from '../../layout/stack/stack-legacy';
import {Link} from '../../link/link';
import {ProgressBar} from '../../progress-bar/progress-bar';
import type {UsageFooterType} from '../../sidebar-nav/types';
import {Body} from '../../typography/body';

import {getUsageMessage, getUsageTitle, getProgressBarColorByType} from '../helpers';

import {UsageFooterUpgradeButton} from './usage-footer-upgrade-button';

type Props = {
    type: UsageFooterType;
    usage: number;
    limit: number;
    approachingLimitPercent: number;
    isBeta?: boolean;
    shouldShowLearnMoreLink?: boolean | null | undefined;
    message?: React.ReactNode;
    featureGated?: boolean;
    onClickFeatureGate?: () => void;
    hideUpgradeCta?: boolean;
    // Optional tooltip for the progress bar
    progressTooltipText?: string;
    aiFeatureType?: AiFeatureType;
};

export function UsageFooter(props: Props) {
    const [showFeatureGateModal, setShowFeatureGateModal] = useState<boolean>(false);

    if (
        !props.featureGated &&
        (props.limit === undefined ||
            props.usage === undefined ||
            props.approachingLimitPercent === undefined)
    ) {
        return null;
    }

    let percentComplete = props.usage / props.limit;
    percentComplete = Math.max(Math.min(percentComplete, 1), 0);
    const approachingLimit = percentComplete >= props.approachingLimitPercent;

    const message =
        props.message || getUsageMessage(props.type, props.usage, props.limit, props.featureGated);

    const shouldUseAiFeatureGate = NutClientConfig.hasAiOutcomesFF && props.type === 'ai';

    return (
        <>
            <Divider variant='line--horizontal' color={colors.navyBgActiveNew} />
            <div className='pad-16 mb-16'>
                <StackLegacy spacing={8}>
                    <Body size='small' color='white'>
                        {getUsageTitle(props.type)}
                    </Body>
                    {props.isBeta ? null : (
                        <Body size='mini' color='white'>
                            <ProgressBar
                                roundedCorners={true}
                                height={6}
                                percentComplete={percentComplete}
                                color={getProgressBarColorByType(
                                    props.type,
                                    percentComplete,
                                    props.approachingLimitPercent,
                                    props.usage,
                                    props.limit
                                )}
                                backgroundColor={colors.offWhiteDk}
                                showPercentage={true}
                                tooltipText={props.progressTooltipText}
                            />
                        </Body>
                    )}
                    <Body size='mini' color='white'>
                        {message}
                    </Body>
                    {props.featureGated && (
                        <UsageFooterUpgradeButton
                            onClick={
                                props.onClickFeatureGate ||
                                (() => {
                                    // if we aren't provided a featureGate, upgrade button links to billing
                                    <Redirect to='/setup/billing/sales' />;
                                })
                            }
                        />
                    )}
                    {approachingLimit && !props.featureGated && !props.hideUpgradeCta && (
                        <Link
                            variant='primary'
                            size='mini'
                            onClick={
                                shouldUseAiFeatureGate
                                    ? () => setShowFeatureGateModal(true)
                                    : undefined
                            }
                            // @ts-expect-error
                            as={shouldUseAiFeatureGate ? undefined : RouterLink}
                            to={shouldUseAiFeatureGate ? undefined : '/setup/billing/sales'}
                        >
                            Upgrade your plan
                        </Link>
                    )}
                    {props.shouldShowLearnMoreLink && (
                        <Link
                            variant='primary'
                            size='mini'
                            href='https://support.nutshell.com/en/articles/8428870-campaigns-cost-of-nutshell-campaigns'
                        >
                            Learn about sending limits
                        </Link>
                    )}
                </StackLegacy>
            </div>
            <AiFeatureGateModal
                isOpen={showFeatureGateModal}
                onClose={() => setShowFeatureGateModal(false)}
                reason='outOfCredits'
                aiFeatureType={props.aiFeatureType ?? AiFeatureType.ZOOM_NOTETAKER}
            />
        </>
    );
}
